'use client';

import { Ranking, RankingType } from '@/types/rankings';
import { getRankingPositionDisplay } from '@/util/ranking';
import { PlayerRanking, SchoolRanking } from '@spikemark/rest-api';
import { BreakpointSwitch } from '@spikemark/ui/components/breakpoint-switch';
import { HelpTooltip } from '@spikemark/ui/components/help-tooltip';
import { MascotWithName } from '@spikemark/ui/components/mascot-with-name';
import { BaseLink as Link, PlayerLink, TeamLink } from '@spikemark/ui/components/navigation/entity-links';
import { PositionDeltaDisplay } from '@spikemark/ui/components/position-delta-display';
import { Table } from '@spikemark/ui/components/table/table';
import { formatRankingPoints, rankToDisplayValue } from '@spikemark/ui/utils/format';
import { ChevronRight } from 'lucide-react';
import { FC, ReactNode, memo } from 'react';
export type RankingsLeaderboardGridProps = Pick<Ranking, 'rankingType' | 'leaderboard'> & {
  compact?: boolean;
  highlightedItem?: any;
  denominationText?: string;
  rankingsMessage?: string;
  rankingsMinimumRounds: number;
  season: string;
};
const InfoLink = () => <p className="-mx-3 border-t border-zinc-200 px-3 pt-2" data-sentry-component="InfoLink" data-sentry-source-file="rankings-leaderboard-grid.tsx">
    <Link href="/information/ranking" className="text-primary" data-sentry-element="Link" data-sentry-source-file="rankings-leaderboard-grid.tsx">
      Read more about Rankings
      <ChevronRight size={16} className="inline-block ml-1" data-sentry-element="ChevronRight" data-sentry-source-file="rankings-leaderboard-grid.tsx" />
    </Link>
  </p>;
export const HeaderWithTooltip = ({
  header,
  tooltipContent,
  className
}: {
  header: ReactNode;
  tooltipContent: ReactNode;
  className?: string;
}) => <div className={className} data-sentry-component="HeaderWithTooltip" data-sentry-source-file="rankings-leaderboard-grid.tsx">
    {header}
    <HelpTooltip className="align-middle" tooltipContent={<div className="py-1">
          <div className="whitespace-break-spaces pb-2">{tooltipContent}</div>

          <InfoLink />
        </div>} data-sentry-element="HelpTooltip" data-sentry-source-file="rankings-leaderboard-grid.tsx" />
  </div>;
const PlayerCellRenderer: FC<{
  rowData: PlayerRanking;
  compact?: boolean;
  season: string;
}> = memo(({
  rowData,
  compact,
  season
}) => <MascotWithName path={rowData.schoolLogo} alt={rowData.schoolName} variant="small" displayMode="team">
      <PlayerLink playerId={rowData.playerId} params={{
    season
  }}>
        {compact ? rowData.displayName : <BreakpointSwitch breakpoint="lg" fallback={rowData.displayName}>
            {rowData.playerName}
          </BreakpointSwitch>}
      </PlayerLink>
    </MascotWithName>);
const TeamCellRenderer: FC<{
  rowData: SchoolRanking;
  compact?: boolean;
  season: string;
}> = memo(({
  rowData,
  compact,
  season
}) => <TeamLink schoolId={rowData.schoolId} params={{
  season
}}>
      <MascotWithName path={rowData.schoolLogo} alt={rowData.boardName!} variant="small" displayMode="team">
        {compact ? rowData.boardName : <BreakpointSwitch breakpoint="lg" fallback={rowData.boardName}>
            {rowData.schoolName}
          </BreakpointSwitch>}
      </MascotWithName>
    </TeamLink>);
export const RankingsLeaderboardGrid: FC<RankingsLeaderboardGridProps> = ({
  rankingType,
  compact,
  leaderboard,
  denominationText,
  rankingsMessage,
  rankingsMinimumRounds,
  season
}) => {
  return <Table<SchoolRanking | PlayerRanking> mode="auto" className="w-full" headerRowClassName="text-xs" compact={compact} columns={[{
    header: !compact && rankingType === RankingType.Player ? <HelpTooltip className="uppercase" tooltipContent={<>
                    <p className="mb-2">
                      A minimum of {rankingsMinimumRounds} rounds must be played in order to be
                      considered for rankings.
                    </p>
                    <InfoLink />
                  </>}>
                Rank
              </HelpTooltip> : 'Rank',
    key: 'rank',
    cellClassName: 'numeric',
    cellRenderer: ({
      rowData
    }) => rankToDisplayValue(getRankingPositionDisplay(rowData).value),
    width: '5%'
  }, {
    header: '+/-',
    cellRenderer: () => <PositionDeltaDisplay change={0} />,
    cellClassName: 'numeric hidden lg:table-cell',
    hidden: true
  }, {
    header: rankingType,
    sortIteratee: rowData => rankingType === RankingType.Player ? (rowData as PlayerRanking).playerName : (rowData as SchoolRanking).schoolName,
    cellRenderer: ({
      rowData
    }) => rankingType === RankingType.Player ? <PlayerCellRenderer rowData={rowData as PlayerRanking} compact={compact} season={season} /> : <TeamCellRenderer rowData={rowData as SchoolRanking} compact={compact} season={season} />
  }, {
    header: 'Team',
    sortIteratee: rowData => rowData.boardName,
    cellClassName: 'hidden lg:table-cell xl:whitespace-nowrap',
    cellRenderer: ({
      rowData
    }) => <TeamLink schoolId={rowData.schoolId} variant="secondary">
              {rowData.boardName}
            </TeamLink>,
    hidden: compact || rankingType !== RankingType.Player
  }, {
    header: 'Wins',
    key: 'eventsWon',
    cellClassName: 'numeric hidden lg:table-cell',
    hidden: compact || rankingType !== RankingType.Player
  }, {
    header: 'Top 3',
    key: 'eventsTop3',
    cellClassName: 'numeric hidden lg:table-cell',
    headerCellClassName: 'whitespace-nowrap',
    hidden: compact || rankingType !== RankingType.Player
  }, {
    header: 'Events',
    cellClassName: 'numeric hidden lg:table-cell',
    cellRenderer: ({
      rowData
    }) => (rowData.strokePlayEvents ?? 0) + (rowData.matchPlayEvents ?? 0)
  }, {
    header: 'Rounds',
    hidden: rankingType !== RankingType.Player,
    cellRenderer: ({
      rowData
    }) => (rowData.strokePlayRounds ?? 0) + (rowData.matchPlayRounds ?? 0),
    cellClassName: 'numeric hidden lg:table-cell'
  }, {
    header: <HelpTooltip tooltipContent={<div className="whitespace-normal">
                  <p className="mb-2">
                    A better win-loss-tie record, all else being equal, will lead to a better
                    ranking.
                  </p>
                  <p className="mb-2">
                    However, since the W-L-T record does not take into account a team's strength of
                    schedule, nor does it consider the margin of victory, it is not a direct input
                    into the ranking.
                  </p>
                  <InfoLink />
                </div>}>
              Record (W-L-T)
            </HelpTooltip>,
    hidden: rankingType !== RankingType.Team,
    cellClassName: 'numeric hidden lg:table-cell whitespace-nowrap',
    cellRenderer: ({
      rowData
    }) => (rowData as SchoolRanking).winLossTie
  }, {
    header: <HeaderWithTooltip header="Avg (w)Pts" tooltipContent={<div className="flex flex-col space-y-2">
                  <p>Your total weighted points divided by your total weighting.</p>
                  <p>{denominationText}</p>
                </div>} />,
    cellClassName: 'numeric font-medium',
    headerCellClassName: 'whitespace-nowrap',
    key: 'averagePoints',
    cellRenderer: ({
      rowData
    }) => formatRankingPoints(rowData.averagePoints)
  }]} stickyHeader stickyTopClassName="top-header-mobile lg:top-header" headerClassName="thead-secondary" rowClassName="text-xs lg:text-sm" data={leaderboard} emptyMessage={<div className="lg:p-8">
          <h3 className="text-xl font-bold mb-2">Rankings Coming Soon</h3>
          {rankingsMessage ? <p>{rankingsMessage}</p> : <p>No rankings data is currently available. Please check back later.</p>}
        </div>} data-sentry-element="Table" data-sentry-component="RankingsLeaderboardGrid" data-sentry-source-file="rankings-leaderboard-grid.tsx" />;
};